export const ORG_LOGOS_BUCKET = 'org-logos';
export const ORG_LOGO_ACCEPT = 'image/jpeg,image/png,image/webp';

const ORG_LOGO_MAX_BYTES = 5 * 1024 * 1024;
const ORG_LOGO_ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

type OrgLogoMimeType = (typeof ORG_LOGO_ALLOWED_MIME_TYPES)[number];

const EXTENSION_BY_MIME_TYPE: Record<OrgLogoMimeType, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

const MIME_TYPE_BY_EXTENSION: Record<string, OrgLogoMimeType> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
};

export function getOrgLogoMimeType(file: File): OrgLogoMimeType | null {
  const mimeType = file.type.toLowerCase();
  if (ORG_LOGO_ALLOWED_MIME_TYPES.includes(mimeType as OrgLogoMimeType)) {
    return mimeType as OrgLogoMimeType;
  }

  const extension = file.name.split('.').pop()?.trim().toLowerCase();
  return extension ? MIME_TYPE_BY_EXTENSION[extension] ?? null : null;
}

export function validateOrgLogoFile(file: File) {
  if (!getOrgLogoMimeType(file)) return 'Upload a JPG, PNG, or WebP logo.';
  if (file.size > ORG_LOGO_MAX_BYTES) return 'Logo must be 5 MB or smaller.';
  return null;
}

export function buildOrgLogoStoragePath(organizationId: string, file: File) {
  const mimeType = getOrgLogoMimeType(file);
  const extension = mimeType ? EXTENSION_BY_MIME_TYPE[mimeType] : 'jpg';
  return `${organizationId}/${createStorageId()}.${extension}`;
}

/** The stored path for a logo this organisation owns, or null for anything else. */
export function extractOrgLogoStoragePath(
  publicUrl: string | null | undefined,
  organizationId: string,
) {
  if (!publicUrl) return null;

  const marker = `/object/public/${ORG_LOGOS_BUCKET}/`;
  const markerIndex = publicUrl.indexOf(marker);
  if (markerIndex === -1) return null;

  const rawPath = publicUrl.slice(markerIndex + marker.length).split('?')[0];
  let decodedPath: string;
  try {
    decodedPath = decodeURIComponent(rawPath);
  } catch {
    return null;
  }

  return decodedPath.startsWith(`${organizationId}/`) ? decodedPath : null;
}

function createStorageId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
